import React, { useEffect, useState, useMemo } from "react";
import CommanTable from "../../components/table/CommanTable";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./userManagement.scss";

import {
  addNewUser,
  deleteUser,
  deleteMultipleUsers,
  getAllUsersManagementData,
  getRoles,
  sendReminderEmail,
  update_user,
  uploadUserBulk,
} from "../../redux/slices/userManagementSlice";

import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from "libphonenumber-js";
import Flags from "react-phone-number-input/flags";

import {
  DeleteOutlined,
  MailOutlined,
  UserOutlined,
  InboxOutlined,
  TeamOutlined,
  WhatsAppOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  setActionName,
  setCloseBtnName,
  setIsPopUpShow,
  setPopUpTitle,
  setSubmitBtnName,
} from "../../redux/slices/popUpSlice";
import { Button, Form, Input, Select, Divider, Upload } from "antd";
import {
  setConfirmationContent,
  setConfirmationData,
  setConfirmationTitle,
  setIsConfirmation,
  setSelectedOption,
  setIsMultipleRecordsSelected,
} from "../../redux/slices/globalSlice";
import Confirmation from "../../components/dialog/Confirmation";
import { isUserManagementDelete } from "../../utils/permissions";
import AppSecureStorage from "../../services/secureStorage";
import { getMobileMaxLength } from "../../utils/mobileMaxLength";

const storage = new AppSecureStorage();

export const UserManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectRole, setSelectedRole] = useState(null);
  const [userData, setUserData] = useState({
    email: "",
    name: "",
    roleId: "",
    contact: "",
    countryCode: null,
  });

  const [status, setStatus] = useState("");
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    roleId: "",
    contact: "",
  });
  const [isRoleChange, setIsRoleChange] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [fileList, setFileList] = useState([]);

  const { getUserManagementData, getRolesData, loading } = useSelector(
    (state) => state.userManagementReducer,
  );
  const { closeBtnName, submitBtnName } = useSelector(
    (state) => state.popUpRedducer,
  );
  const {
    page,
    limit,
    filterValue,
    sortConfig,
    searchInput,
    selectedOptionData,
    selectedUserRoleId,
    selectedRoleId,
  } = useSelector((state) => state.globalReducer);

  // const [visibleColumns, setVisibleColumns] = useState({
  // sNo: true,
  // name: true,
  // number: true,
  // email: true,
  // role: true,
  // });

  const [visibleColumns, setVisibleColumns] = useState(() => {
    // Load from localStorage on mount, fallback to default
    const saved = storage.get(`visibleColumns_${"userManagement"}`); // Use title for per-table persistence
    return saved
      ? JSON.parse(saved)
      : {
          sNo: true,
          name: true,
          number: true,
          email: true,
          role: true,
        };
  });

  useEffect(() => {
    storage.set(
      `visibleColumns_${"userManagement"}`,
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns]);

  const validateInput = (name, value) => {
    let error = "";

    switch (name) {
      case "email":
        if (!value) {
          error = "Please enter your email address";
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;

      case "name":
        if (!value) {
          error = "Please enter your name";
        } else if (value.length < 3) {
          error = "Name must be at least 3 characters";
        } else if (!/^[A-Za-z ]+$/.test(value)) {
          error = "Name can only contain letters & spaces";
        } else if (value.length > 50) {
          error = "Name cannot exceed 50 characters";
        }
        break;
      case "roleId":
        if (!value) {
          error = "Please select a role";
        }
        break;
      case "countryCode":
        if (!value) {
          error = "Country code is required";
        } else {
          error = null;
        }
        break;
      case "contact":
        if (value) {
          const fullNumber = `+${getCountryCallingCode(userData.countryCode)}${value}`;
          if (!isValidPhoneNumber(fullNumber)) {
            error = "Please enter a valid WhatsApp number";
          }
        } else {
          error = "WhatsApp number is required ";
        }
        break;
      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleChange = (value) => {
    const selectedRoleObject = getRolesData?.data?.find(
      (role) => role?.name === value,
    );
    if (selectedRoleObject) {
      setSelectedRole(value);
      setUserData((prevState) => ({
        ...prevState,
        roleId: selectedRoleObject?._id,
      }));

      validateInput("roleId", selectedRoleObject._id);
    }
  };

  const handleChangeInput = (e) => {
    const { name, value } = e.target;

    setUserData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    validateInput(name, value.trim());
  };

  useEffect(() => {
    dispatch(
      getAllUsersManagementData({
        page,
        limit,
        navigate,
        filterValue: filterValue?.toLowerCase(),
        searchQuery: searchInput || "",
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction,
      }),
    );
    dispatch(getRoles({ navigate: navigate }));
  }, []);

  const dataSource = useMemo(() => {
    return getUserManagementData?.data.map((item) => ({
      ...item,
      key: item._id,
    }));
  }, [getUserManagementData?.data]);

  const openConfirmationForRole = () => {
    setIsRoleChange(true);
    dispatch(setConfirmationContent(`Are you sure you want to change role`));
    dispatch(setConfirmationTitle("Update User Role"));
    dispatch(setIsConfirmation(true));
  };

  const confirmationOpen = (record) => {
    dispatch(setSelectedOption(record));
    dispatch(setConfirmationTitle("Remove User"));
    dispatch(setConfirmationContent("Are you sure You want to remove"));
    dispatch(setConfirmationData(record?.name));
    dispatch(setIsConfirmation(true));
  };

  const multiDeleteConfirmationOpen = () => {
    if (!selectedRowKeys.length) return;

    const plural = selectedRowKeys.length > 1 ? "s" : "";
    const title = `Delete User${plural}`;
    const content = `Are you sure you want to delete ${selectedRowKeys.length} user${plural}`;

    setStatus("delete");
    dispatch(setSelectedOption(null));
    dispatch(setConfirmationTitle(title));
    dispatch(setConfirmationContent(content));
    dispatch(setConfirmationData(selectedRowKeys));
    dispatch(setIsConfirmation(true));
    dispatch(setIsMultipleRecordsSelected(true));
  };

  const actionsOptions = [
    isUserManagementDelete && {
      label: "Delete",
      acttion: (record) => {
        setStatus("delete");
        confirmationOpen(record);
      },
      icon: <DeleteOutlined />,
    },
  ];

  const handlePopUpOpen = () => {
    dispatch(setCloseBtnName("Close"));
    dispatch(setSubmitBtnName("Submit"));
    dispatch(setActionName("Add User"));
    dispatch(setPopUpTitle("Add User"));
    dispatch(setIsPopUpShow(true));
  };

  const beforeUploadFile = (file) => {
    setFileList([file]);
    return false;
  };

  const onRemoveFile = () => {
    setFileList([]);
  };

  const options = getRolesData?.data?.map((role) => ({
    value: role.name,
    label: role.name,
  }));

  const renderPopupContent = () => {
    const regionNames = new Intl.DisplayNames(["en"], {
      type: "region",
    });

    const countryOptions = getCountries()
      .map((country) => ({
        value: country,
        label: regionNames.of(country) || country,
        dialCode: `+${getCountryCallingCode(country)}`,
        searchLabel:
          `${regionNames.of(country)} ${country} +${getCountryCallingCode(country)}`.toLowerCase(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const maxLength = getMobileMaxLength(userData?.countryCode);

    const handleSampleFile = () => {
      const link = document.createElement("a");
      link.href = "/sample_users_bulk_upload.csv";
      link.download = "sample_users_bulk_upload.csv";
      link.click();
    };

    return (
      <div>
        <Form.Item required label="Name" layout="vertical">
          <Input
            size="large"
            name="name"
            value={userData?.name}
            onChange={handleChangeInput}
            placeholder="Enter Name"
            status={errors?.name ? "error" : ""}
            prefix={<UserOutlined />}
            showCount
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </Form.Item>
        <Form.Item required label="Whatsapp Contact Number" layout="vertical">
          <div className="country-code-contact-container">
            <Select
              name="countryCode"
              style={{ width: 220, height: 40 }}
              showSearch
              size="large"
              placeholder="Select Country Code"
              value={userData?.countryCode}
              optionFilterProp="searchLabel"
              options={countryOptions.map((item) => {
                const Flag = Flags[item.value];
                return {
                  value: item.value,
                  searchLabel: item.searchLabel,
                  label: (
                    <div className="lable-container">
                      {Flag && (
                        <Flag
                          style={{
                            width: 22,
                            height: 16,
                            borderRadius: 2,
                            boxShadow: "0 0 1px rgba(0,0,0,.3)",
                          }}
                        />
                      )}
                      <span>{item.value}</span>
                      <span>{item.dialCode}</span>
                    </div>
                  ),
                };
              })}
              onChange={(value) =>
                handleChangeInput({
                  target: { name: "countryCode", value: value },
                })
              }
            />
            {errors?.countryCode && (
              <div style={{ width: 200 }} className="error-message">
                {errors.countryCode}
              </div>
            )}

            <div className="contact-number-container">
              <Input
                size="large"
                name="contact"
                value={userData?.contact}
                onChange={handleChangeInput}
                placeholder="Enter Number"
                prefix={<WhatsAppOutlined />}
                maxLength={maxLength}
                disabled={!userData.countryCode}
                className="contact-number-container"
              />
              {errors?.contact && (
                <div className="error-message">{errors.contact}</div>
              )}
            </div>
          </div>
        </Form.Item>

        <div className="email-role-containers">
          <Form.Item
            required
            label="Email"
            layout="vertical"
            wrapperCol={{ span: 24 }}
            className="email-container"
          >
            <Input
              size="large"
              name="email"
              type="email"
              value={userData?.email}
              onChange={handleChangeInput}
              placeholder="Enter Email"
              status={errors?.email ? "error" : ""}
              prefix={<MailOutlined />}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </Form.Item>

          <Form.Item
            required
            label="Role"
            layout="vertical"
            className="role-container"
          >
            <Select
              value={selectRole}
              size="large"
              onChange={handleChange}
              style={{ width: "100%" }}
              allowClear
              status={errors?.roleId ? "error" : ""}
              options={options}
              placeholder="Select Role"
            />
            {errors.roleId && (
              <span className="error-message">{errors.roleId}</span>
            )}
          </Form.Item>
        </div>
        <Divider style={{ margin: "-4px" }}>OR</Divider>
        <div className="sample-file-download">
          <Button icon={<DownloadOutlined />} onClick={handleSampleFile}>
            Download CSV Format
          </Button>
        </div>
        <Upload.Dragger
          fileList={fileList}
          multiple={false}
          beforeUpload={beforeUploadFile}
          onRemove={onRemoveFile}
          accept="text/csv"
          listType="text"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Add or Edit users in bulk</p>
          <p className="ant-upload-hint">
            Select a csv file with user data to upload users in bulk. Click or
            drag file to this area
          </p>
        </Upload.Dragger>
      </div>
    );
  };

  const handleOk = () => {
    setErrors({ ...errors, name: "", email: "", roleId: "" });
    setUserData({
      email: "",
      name: "",
      roleId: "",
      contact: "",
      countryCode: null,
    });
    setSelectedRole("");
    dispatch(setIsPopUpShow(false));
  };

  const validateForm = () => {
    validateInput("name", userData.name);
    validateInput("email", userData.email);
    validateInput("roleId", userData.roleId);
    validateInput("contact", userData.contact);
    validateInput("countryCode", userData.countryCode);

    return (
      !errors.name &&
      !errors.email &&
      !errors.roleId &&
      !errors.contact &&
      !errors.countryCode
    );
  };

  const onSubmit = () => {
    if (fileList.length) {
      const formData = new FormData();
      formData.append("file", fileList[0]);

      dispatch(
        uploadUserBulk({
          body: formData,
          page: page,
          navigate: navigate,
          limit: limit,
          filterValue: filterValue?.toLowerCase(),
          searchQuery: searchInput || "",
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction,
        }),
      ).then(() => setFileList([]));
    } else if (validateForm()) {
      dispatch(
        addNewUser({
          body: {
            ...userData,
            countryCode: getCountryCallingCode(userData.countryCode),
            name: userData.name
              .trim()
              .split(/\s+/)
              .map(
                (word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
              )
              .join(" "),
          },
          page: page,
          navigate: navigate,
          limit: limit,
          filterValue: filterValue?.toLowerCase(),
          searchQuery: searchInput || "",
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction,
        }),
      ).then((error) => {
        console.log(error, "error aa t");
        // Reset user creation state after successful creation
        if (error) {
          console.log("error", error);
        } else {
          handleOk();
        }
      });
    }
  };

  const canSubmit = () => {
    if (fileList.length) return true;
    const hasContact =
      userData.contact && String(userData.contact).trim() !== "";
    const hasCountryCode =
      userData.countryCode && String(userData.countryCode).trim() !== "";

    const whatsappValid = hasContact && hasCountryCode;

    if (
      userData.name &&
      userData.email &&
      userData.roleId &&
      whatsappValid &&
      errors.name === "" &&
      errors.email === "" &&
      errors.roleId === "" &&
      errors.contact === "" &&
      errors.countryCode === null
    ) {
      return true;
    }

    return false;
  };

  const renderFooter = () => (
    <div className="button-groups">
      <Button onClick={handleOk}>{closeBtnName}</Button>
      <Button
        className="add-user-submit-btn"
        disabled={!canSubmit()}
        onClick={onSubmit}
      >
        {submitBtnName}
      </Button>
    </div>
  );

  const handleRemove = () => {
    dispatch(
      deleteUser({
        id: selectedOptionData?._id,
        page: page,
        limit: limit,
        navigate: navigate,
        filterValue: filterValue?.toLowerCase(),
        searchQuery: searchInput || "",
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction,
      }),
    );
    dispatch(setIsConfirmation(false));
  };

  const handleRemoveMultiple = () => {
    dispatch(
      deleteMultipleUsers({
        userIds: selectedRowKeys,
        page: page,
        limit: limit,
        navigate: navigate,
        filterValue: filterValue?.toLowerCase(),
        searchQuery: searchInput || "",
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction,
      }),
    ).then(() => {
      dispatch(setIsConfirmation(false));
      setSelectedRowKeys([]);
    });
  };

  const handlesendRminder = () => {
    if (isRoleChange) {
      const body = {
        roleId: selectedRoleId,
      };
      dispatch(
        update_user({
          id: selectedUserRoleId,
          data: body,
          navigate: navigate,
          page: page,
          limit: limit,
          filterValue: filterValue?.toLowerCase(),
          searchQuery: searchInput || "",
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction,
        }),
      );
      dispatch(setIsConfirmation(false));
      setIsRoleChange(false);
    } else {
      dispatch(
        sendReminderEmail({
          id: selectedOptionData?._id,
          navigate: navigate,
        }),
      );
      dispatch(setIsConfirmation(false));
    }
  };

  const rowSelection = isUserManagementDelete
    ? {
        onChange: setSelectedRowKeys,
        selectedRowKeys,
      }
    : null;

  return (
    <>
      <CommanTable
        title="User"
        titlePrefix="Management"
        data={dataSource}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        apiCall={(page, limit, searchInput, sortKey, sortDirection) => {
          dispatch(
            getAllUsersManagementData({
              page: page,
              limit: limit,
              navigate,
              filterValue: filterValue?.toLowerCase(),
              searchQuery: searchInput,
              sortKey: sortKey || "name",
              sortDirection: sortDirection || "asc",
            }),
          );
        }}
        actionsOptions={actionsOptions}
        openAddPopup={handlePopUpOpen}
        showDeleteButton={!!selectedRowKeys.length}
        openDeletePopup={multiDeleteConfirmationOpen}
        showDeleteSelection={selectedRowKeys.length > 0}
        icon={<TeamOutlined className="icon-size text-white" />}
        onClose={() => {
          setErrors({
            ...errors,
            name: "",
            email: "",
            roleId: "",
            contact: "",
            countryCode: "",
          });
          dispatch(setIsPopUpShow(false));
          setUserData({
            ...userData,
            email: "",
            roleId: "",
            name: "",
            contact: "",
            countryCode: "",
          });
          setSelectedRole("");
        }}
        renderPopupContent={renderPopupContent}
        isFooter={true}
        customFooter={renderFooter}
        isAdd={true}
        loading={loading}
        onChangeRole={() => {
          openConfirmationForRole();
        }}
        rowSelection={rowSelection}
        width={600}
      />
      <Confirmation
        isRemove={status === "reminder" || isRoleChange ? false : true}
        onRemove={handleRemove}
        onRemoveMultiple={handleRemoveMultiple}
        onSubmit={handlesendRminder}
      />
    </>
  );
};
